import styled from "@emotion/styled";
import {
	MenuItem,
	Select,
	type SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import * as React from "react";
import type { RefCallBack } from "react-hook-form";

import type { LookupItem } from "Infrastructure/Models/LookupItem";
import {
	GlobalResources,
	useResource,
} from "Infrastructure/Translations/Resources";
import { ExpandArrowIcon } from "Shared/Icons";
import { BlCheckboxInput } from "Shared/Inputs/BlCheckboxInput";

type valueType = string | number | string[] | number[];

type Props = {
	name: string;
	options: LookupItem[];
	value: valueType;
	defaultValue?: valueType;
	onChange?: (value: valueType) => void;
	onBlur?: () => void;
	disabled?: boolean;
	multiple?: boolean;
	label?: string;
	placeholder?: string;
	allSelectedText?: string;
	hasError?: boolean;
	hasFilterStyle?: boolean;
	inputRef?: RefCallBack;
};

// Styled components
const StyledSelect = styled(Select)<{
	$hasFilterStyle?: boolean;
	$disabled?: boolean;
}>`
  &:before,
  &:after {
    content: none;
  }

  .MuiSelect-select {
    padding: 8px 32px 8px 12px;
    border-radius: ${(props) => props.theme.borderRadius};
		height: 24px;
		font-size: 16px;
		font-weight: 400;
		line-height: 1.4;
		color: #233145;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.colors.border};
    border-radius: ${(props) => props.theme.borderRadius};
  }

  &:not(.Mui-disabled):hover {
    .MuiOutlinedInput-notchedOutline {
      border-color: ${(props) => props.theme.palette.primary.main};
    }
  }

  &.Mui-focused {
    .MuiOutlinedInput-notchedOutline {
      border-color: ${(props) => props.theme.palette.primary.main};
    }
  }

  &.Mui-disabled {
    .MuiSelect-select {
      background-color: ${(props) => props.theme.colors.gray}80;
      border-color: ${(props) => props.theme.colors.gray}80;
      color: ${(props) => props.theme.palette.text.primary}80;
    }
  }

  ${(props) =>
		props.$hasFilterStyle &&
		`
    .MuiSelect-select {
      color: ${props.theme.palette.primary.main};
      background-color: transparent;
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
  `}
`;

const StyledLabel = styled.div`
  color: ${(props) => props.theme.palette.text.primary};
  margin-bottom: ${(props) => props.theme.spacing(1)};
`;

const StyledIcon = styled.div<{ $hasFilterStyle: boolean }>`
  position: absolute;
  right: 16px;
  pointer-events: none;
  display: flex;
  align-items: center;
  color: ${(props) =>
		props.$hasFilterStyle
			? props.theme.palette.primary.main
			: props.theme.palette.text.primary};

  svg {
    width: 12px;
    height: auto;
    opacity: ${(props) => (props.$hasFilterStyle ? 1 : 0.5)};
  }
`;

const StyledSelectedValue = styled.div<{
	$hasFilterStyle: boolean;
	$upperCase?: boolean;
}>`
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: ${(props) => (props.$upperCase ? "uppercase" : "none")};
`;

const dropdownIcon = (hasFilterStyle: boolean) => {
	return (
		<StyledIcon $hasFilterStyle={hasFilterStyle}>
			<ExpandArrowIcon />
		</StyledIcon>
	);
};

const StyledPlaceholder = styled.div<{ $hasFilterStyle: boolean }>`
  font-size: ${(props) => (props.$hasFilterStyle ? "12px" : "16px")};
  margin-right: ${(props) =>
		props.$hasFilterStyle ? props.theme.spacing(1) : props.theme.spacing(2)};
  font-weight: ${(props) => (props.$hasFilterStyle ? 600 : 400)};
  opacity: ${(props) => (props.$hasFilterStyle ? 1 : 0.5)};
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: 14px;
	font-weight: 400;
  .MuiTouchRipple-root {
    color: ${(props) => props.theme.palette.primary.main};
  }

  .MuiCheckbox-root {
    padding: 0;
  }
`;

const selectedValue = (
	value: valueType,
	props: Props,
	t: (resourcePath: string, options?: any) => string,
): React.ReactNode => {
	const { placeholder, multiple, options, allSelectedText } = props;

	if (!value || (value as Array<string | number>).length === 0) {
		return placeholder ? (
			<StyledPlaceholder $hasFilterStyle={props.hasFilterStyle ?? false}>
				{placeholder}
			</StyledPlaceholder>
		) : (
			<StyledSelectedValue $hasFilterStyle={props.hasFilterStyle ?? false} />
		);
	}

	if (multiple && (value as Array<string | number>).length === options.length) {
		return (
			<StyledSelectedValue $hasFilterStyle={props.hasFilterStyle ?? false}>
				{allSelectedText ?? t(GlobalResources.Common.AllStatuses)}
			</StyledSelectedValue>
		);
	}
	const text = multiple
		? options
				.filter((o) => (value as number[]).some((v) => v === o.Value))
				.map((x) => x.Name)
				.join(", ")
		: options.find((x) => x.Value.toString() === value.toString())?.Name;

	return (
		<StyledSelectedValue $hasFilterStyle={props.hasFilterStyle ?? false}>
			{text}
		</StyledSelectedValue>
	);
};

export const BlDropdown: React.FunctionComponent<Props> = (props) => {
	const {
		options,
		value,
		disabled,
		name,
		onChange,
		multiple,
		label,
		onBlur,
		hasError,
		hasFilterStyle,
		inputRef,
	} = props;
	const { t } = useResource();
	const menuAnchorRef = React.useRef<HTMLDivElement>(null);

	const handleChange = (event: SelectChangeEvent<unknown>) => {
		onChange?.(event.target.value as valueType);
	};

	const anchorMenuProps =
		hasFilterStyle && menuAnchorRef.current
			? {
					anchorEl: menuAnchorRef.current,
				}
			: {};

	return (
		<div>
			{!!label && (
				<StyledLabel>
					<Stack direction={"row"} gap={0.5}>
						<Typography>{label}</Typography>
					</Stack>
				</StyledLabel>
			)}
			<StyledSelect
				value={value}
				defaultValue={props.defaultValue}
				onChange={handleChange}
				onBlur={onBlur}
				inputProps={{ "aria-label": "Without label" }}
				IconComponent={() => dropdownIcon(hasFilterStyle ?? false)}
				disabled={disabled}
				multiple={multiple}
				error={hasError}
				fullWidth
				displayEmpty
				MenuProps={{
					variant: "menu",
					style: {
						maxHeight: "300px",
						maxWidth: "350px",
					},
					slotProps: {
						list: {
							sx: {
								"& .MuiMenuItem-root": {
									whiteSpace: "normal",
								},
							},
						},
					},
					...anchorMenuProps,
				}}
				renderValue={(value) => selectedValue(value as valueType, props, t)}
				variant={"outlined"}
				inputRef={inputRef}
				$hasFilterStyle={hasFilterStyle}
				$disabled={disabled}
			>
				{options.map((opt) => (
					<StyledMenuItem key={`${name}-${opt.Value}`} value={opt.Value}>
						{multiple ? (
							<BlCheckboxInput
								checked={(value as number[]).some((x) => x === opt.Value)}
								label={opt.Name}
							/>
						) : (
							<span>{opt.Name}</span>
						)}
					</StyledMenuItem>
				))}
			</StyledSelect>
			<div ref={hasFilterStyle ? menuAnchorRef : undefined} />
		</div>
	);
};
